/**
 * /logistica/instalacoes — Lista de OrdensInstalacao com agenda por data.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiAlertTriangle, FiPlus, FiRefreshCw } from "react-icons/fi";

import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import { useAuth } from "../../../context/AuthContext";
import {
  listarOis,
  SITUACAO_OI_LABELS,
  SITUACAO_OI_TONES,
} from "../../../services/logistica";
import { PageContainer } from "../../../styles/shared";

import NovaOiForm from "./NovaOiForm";

import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Input,
  Pill,
  SectionTitle,
  Select,
  Table,
  TableWrapper,
} from "../../Orcamentos/styles";


function hojeISO() {
  return new Date().toISOString().slice(0, 10);
}


export default function InstalacoesLista() {
  const navigate = useNavigate();
  const { request, hasAllPermissions, isMaster } = useAuth();
  const podePlanejar = isMaster || hasAllPermissions(["logistica:planejar"]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filtroSituacao, setFiltroSituacao] = useState("");
  const [filtroData, setFiltroData] = useState("");
  const [criando, setCriando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = await listarOis(request, {
        situacao: filtroSituacao || undefined,
        data: filtroData || undefined,
      });
      setItems(Array.isArray(p) ? p : p?.results || []);
    } catch (err) {
      setError(err?.message || "Erro ao carregar instalações.");
      setItems([]);
    } finally { setLoading(false); }
  }, [request, filtroSituacao, filtroData]);

  useEffect(() => { carregar(); }, [carregar]);

  const kpis = useMemo(() => {
    const agendadas = items.filter((o) => o.situacao === "AGENDADA").length;
    const emExecucao = items.filter((o) => o.situacao === "EM_EXECUCAO").length;
    const concluidas = items.filter((o) => o.situacao === "CONCLUIDA").length;
    return [
      { label: "Total", value: items.length, tone: "neutral" },
      { label: "Agendadas", value: agendadas, tone: agendadas > 0 ? "info" : "neutral" },
      { label: "Em execução", value: emExecucao, tone: emExecucao > 0 ? "warning" : "neutral" },
      { label: "Concluídas", value: concluidas, tone: "success" },
    ];
  }, [items]);

  return (
    <PageContainer>
      <EntityHeader
        title="Ordens de instalação"
        subtitle="Agenda das equipes de campo (ADR-0026)"
        actions={[
          ...(podePlanejar
            ? [{
                label: "Agendar instalação",
                icon: <FiPlus size={14} />,
                onClick: () => setCriando(true),
              }]
            : []),
          {
            label: "Atualizar",
            icon: <FiRefreshCw size={14} />,
            onClick: carregar,
          },
        ]}
      />

      <KpiStrip items={kpis} />

      <Card>
        <SectionTitle>Filtros</SectionTitle>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 180 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Situação</span>
            <Select value={filtroSituacao} onChange={(e) => setFiltroSituacao(e.target.value)}>
              <option value="">Todas</option>
              {Object.entries(SITUACAO_OI_LABELS).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </Select>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 180 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Data agendada</span>
            <Input
              type="date" value={filtroData}
              onChange={(e) => setFiltroData(e.target.value)}
            />
          </label>
          <button
            type="button"
            onClick={() => { setFiltroData(hojeISO()); setFiltroSituacao(""); }}
            style={{
              padding: "8px 14px",
              border: "1px solid var(--border)",
              borderRadius: 6,
              background: "var(--surface)",
              cursor: "pointer", marginTop: 20, color: "inherit",
            }}
          >
            Hoje
          </button>
        </div>
      </Card>

      {error && <ErrorMsg><FiAlertTriangle size={14} /> {error}</ErrorMsg>}

      <Card>
        <SectionTitle>{loading ? "Carregando..." : `${items.length} OI(s)`}</SectionTitle>
        {!loading && items.length === 0 && <EmptyMsg>Nenhuma OI.</EmptyMsg>}
        {items.length > 0 && (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Pedido</th>
                  <th>Cliente</th>
                  <th>Equipe</th>
                  <th>Data</th>
                  <th>Endereço</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {items.map((oi) => (
                  <tr
                    key={oi.id}
                    onClick={() => navigate(`/logistica/instalacoes/${oi.id}`)}
                    style={{ cursor: "pointer" }}
                  >
                    <td><strong>{oi.numero}</strong></td>
                    <td>{oi.pedido_numero}</td>
                    <td>{oi.pedido_cliente_nome}</td>
                    <td>{oi.equipe_nome}</td>
                    <td>{oi.data_agendada}</td>
                    <td style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={oi.endereco}>
                      {oi.endereco}
                    </td>
                    <td>
                      <Pill $tone={SITUACAO_OI_TONES[oi.situacao] || "neutral"}>
                        {SITUACAO_OI_LABELS[oi.situacao] || oi.situacao}
                      </Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>
      <NovaOiForm
        aberto={criando}
        onFechar={() => setCriando(false)}
        onCriada={carregar}
      />
    </PageContainer>
  );
}
